/**
 * Config Import Definition API (New API).
 * Alle kall går mot /api/import-definition* og proxy til ICS.API.
 * Krever Ics-Project-Id og Ics-DbName (satt av axiosClient).
 */
import axiosClient from '../axiosClient';
import type {
  ImportDefinitionResponse,
  UpdateImportDefinitionStepsRequest
} from '@/api/contracts/importApi';
import type {
  FromSchemaOrFallback,
  ImportDefinitionRequestFallback,
  ImportColumnDefinitionRequestFallback
} from '@/api/types/schema-fallbacks';

export type { ImportDefinitionResponse, UpdateImportDefinitionStepsRequest };
export type ImportDefinitionRequest = FromSchemaOrFallback<'ImportDefinitionRequest', ImportDefinitionRequestFallback>;
export type ImportColumnDefinitionRequest = FromSchemaOrFallback<'ImportColumnDefinitionRequest', ImportColumnDefinitionRequestFallback> & { isCommentColumn?: boolean };
export interface AddImportColumnDefinitionRequest {
  columns: Array<{
    sourceColumnName?: string;
    targetColumnKey?: string;
    ordinal: number;
  }>;
}

function normalizeImportDefinition (raw: ImportDefinitionResponse | null | undefined): ImportDefinitionResponse | null {
  if (!raw) {
    return null
  }

  const normalizedTableKey = String(raw.tableKey || '').trim() || null

  return {
    ...raw,
    tableKey: normalizedTableKey
  }
}

export async function getImportDefinition (importDefinitionKey: string): Promise<ImportDefinitionResponse | null> {
  const response = await axiosClient.get<ImportDefinitionResponse>('import-definition', {
    params: { importDefinitionKey }
  });
  return normalizeImportDefinition(response.data);
}

/** Henter aktive importdefinisjoner for tabell. `tableKey` brukes i FE. */
export async function getImportDefinitions (tableKey: string): Promise<ImportDefinitionResponse[]> {
  const response = await axiosClient.get<ImportDefinitionResponse[]>('import-definitions', {
    params: { tableKey }
  });
  return (response.data ?? [])
    .map((item) => normalizeImportDefinition(item))
    .filter((item): item is ImportDefinitionResponse => item !== null);
}

export async function saveImportDefinition (request: ImportDefinitionRequest): Promise<string> {
  const response = await axiosClient.post<string>('import-definition', request);
  return response.data;
}

export async function deleteImportDefinition (importDefinitionKey: string): Promise<void> {
  await axiosClient.delete('import-definition', { params: { importDefinitionKey } });
}

export async function addImportDefinitionColumns (
  importDefinitionKey: string,
  request: AddImportColumnDefinitionRequest
): Promise<void> {
  await axiosClient.post(`import-definition/${importDefinitionKey}/add-columns`, request);
}

export async function updateImportDefinitionColumn (
  importDefinitionKey: string,
  request: ImportColumnDefinitionRequest
): Promise<void> {
  await axiosClient.put(`import-definition/${importDefinitionKey}/column`, request);
}

export async function updateImportDefinitionSteps (
  importDefinitionKey: string,
  request: UpdateImportDefinitionStepsRequest
): Promise<void> {
  await axiosClient.put(`import-definition/${importDefinitionKey}/steps`, request);
}

export async function updateImportDefinitionTable (
  importDefinitionKey: string,
  request: { tableKey: string }
): Promise<void> {
  await axiosClient.put(`import-definition/${importDefinitionKey}/table`, request);
}
